/**
 * Shared Axios HTTP client for V2 Online Booking API.
 *
 * Provides two clients:
 *   - `publicClient`  – for unauthenticated / public endpoints
 *   - `authClient`    – auto-attaches `Authorization: Bearer <token>` from auth session
 *
 * Both clients share the same base URL, timeout, and domain headers.
 *
 * After account resolution, call `setAccountSlug(slug)` to inject
 * `X-Account-Slug` into every outgoing request across all Axios instances.
 */

import axios from 'axios'
import { getApiCommonHeaders, API_BASE_URL, DEFAULT_API_TIMEOUT_MS } from '@/config/api'

const TIMEOUT_MS = Number.isFinite(DEFAULT_API_TIMEOUT_MS) ? DEFAULT_API_TIMEOUT_MS : 10000

// ---------------------------------------------------------------------------
// Account slug — set once after the account API resolves
// Uses window as the source-of-truth so every Vite chunk sees the same value.
// ---------------------------------------------------------------------------
const SLUG_HEADER = 'X-Account-Slug'
const _instances = [] // all Axios instances registered via addSlugInterceptor

/**
 * Store the resolved account slug so every subsequent request
 * includes `X-Account-Slug`.  Call this once from main.js.
 *
 * The slug is stored on `window` to guarantee a single source-of-truth
 * across all code-split chunks.  Additionally, the slug is pushed into
 * every registered Axios instance's default headers for maximum reliability.
 */
export function setAccountSlug(slug) {
  const value = slug || null

  // 1. Global source of truth — readable from any module / chunk
  if (typeof window !== 'undefined') {
    window.__gopgms_account_slug = value
  }

  // 2. Push into every registered Axios instance's defaults
  _instances.forEach((instance) => {
    if (value) {
      instance.defaults.headers[SLUG_HEADER] = value
    } else {
      delete instance.defaults.headers[SLUG_HEADER]
    }
  })
}

/** Read the current slug (safe to call from anywhere). */
export function getAccountSlug() {
  return (typeof window !== 'undefined' && window.__gopgms_account_slug) || null
}

/**
 * Install the `X-Account-Slug` request interceptor on any Axios instance.
 * Also registers the instance so `setAccountSlug` can update its defaults.
 */
export function addSlugInterceptor(axiosInstance) {
  _instances.push(axiosInstance)

  // If slug was already set before this instance was registered, apply it now
  const current = getAccountSlug()
  if (current) {
    axiosInstance.defaults.headers[SLUG_HEADER] = current
  }

  // Belt-and-suspenders: also set via request interceptor at call time
  axiosInstance.interceptors.request.use((config) => {
    const slug = getAccountSlug()
    if (slug) {
      // Axios 1.x: use .set() if available, fallback to bracket notation
      if (typeof config.headers?.set === 'function') {
        config.headers.set(SLUG_HEADER, slug)
      } else {
        config.headers[SLUG_HEADER] = slug
      }
    }
    return config
  })
}

// ---------------------------------------------------------------------------
// Public client — no auth header
// ---------------------------------------------------------------------------
export const publicClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    ...getApiCommonHeaders()
  }
})

addSlugInterceptor(publicClient)

// ---------------------------------------------------------------------------
// Authenticated client — attaches JWT from localStorage
// ---------------------------------------------------------------------------
const SESSION_STORAGE_KEY = 'sanwaria_auth_session_v1'

const readToken = () => {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed?.token || ''
  } catch {
    return ''
  }
}

export const authClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    ...getApiCommonHeaders()
  }
})

addSlugInterceptor(authClient)

// Request interceptor: attach Bearer token on every request
authClient.interceptors.request.use((config) => {
  const token = readToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})
